import React, { useMemo } from "react";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { ContractLight, VisibleColumnSpec } from "@/types";
import { useTranslation } from "react-i18next";
import NonSortableHeader from "@/components/molecules/NonSortableHeader";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import DataTable from "@/components/molecules/DataTable";
import { useVisibleColumns } from "@/hooks/useVisibleColumns";
import { useGetContractsLight } from "@/services/Contracts";
import Group from "@/components/atoms/Group";
import { Menu } from "@mantine/core";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import styles from "./styles.module.css";
import ContractSortTypeEnum from "@/constants/enums/ContractSortTypeEnum";

dayjs.extend(advancedFormat);

interface ContractLightTableProps {
  /** Define a column spec of what rows and columns to display and when **/
  visibleColumns?: VisibleColumnSpec<ContractLight, "price" | "actions">[];
  /** Define a predefined sort order **/
  initialSort?: SortingState;
  /** Whether to show client-side pagination */
  pagination?: boolean;
  /** Decide whether to enable or disable actions **/
  actionable?: boolean;
  /** Optional rows per page for client-side pagination */
  initialRowsPerPage?: number;
  /** Local rows to use for Bulk edit of contracts **/
  localRows?: ContractLight[];
}

/**
 * A reusable task table component, with sorting, filtering, actions and pagination.
 * @param TaskTableProps - Component props
 * @param visibleColumns - Constant Array defined to manage which columns to display and when
 * @param initialSort - Define a predefined sort order
 * @param pagination - Boolean to enable or disable pagination
 * @param actionable - Boolean to enable or disable row actions
 * @param initialRowsPerPage - set maximum number of rows to display used for snapshots
 *
 **/
const ArchivedContractTable: React.FC<ContractLightTableProps> = ({
  visibleColumns,
  initialSort = null,
  pagination: _ = true,
  actionable: _1 = true,
  initialRowsPerPage = 15,
  localRows = null,
}) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { currentCompany } = useAuth();

  const { data: contracts } = useGetContractsLight(
    {
      // FAKING IT this is only used in Bulk update which has local rows only for now
      sortOption: ContractSortTypeEnum.BY_DATE,
      limit: 100,
      showDeleted: true,
      sortDirection: "ASC",
      pageNumber: 0,
      query: "",
    },
    {
      enabled: !!currentCompany?.companyId && !localRows,
    }
  );

  const baseColumns: ColumnDef<ContractLight>[] = useMemo(
    () => [
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("common:id")} />,
        id: "id",
        cell: ({ row }) => {
          return row.original.id;
        },
        size: 25,
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("contracts:contractDate")} />,
        id: "contractDate",
        cell: ({ row }) => {
          return dayjs(row.original.contractDate).format("Do MMMM YYYY");
        },
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("contracts:contractName")} />,
        id: "name",
        cell: ({ row }) => {
          return row.original.name;
        },
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("product:product")} />,
        id: "contractProduct",
        cell: ({ row }) => {
          return row.original.contractProduct.companySpecificProduct.companySpecificProductName;
        },
      },
      {
        header: () => <></>,
        id: "actions",
        size: 50,
        minSize: 50,
        maxSize: 50,
        cell: ({ row }) => {
          return (
            <Group justify={"flex-end"} w={"100%"}>
              <Menu offset={0} position={"bottom-end"}>
                <Menu.Target>
                  <span style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                    <Group align={"center"} justify={"center"}>
                      <SVGAsset name="dotdotdot-grey" />
                    </Group>
                  </span>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    style={{ width: "auto" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/contracts/details/${row.original.id}`);
                    }}
                  >
                    <Group>{t("contracts:showDetails")}</Group>
                  </Menu.Item>
                  {/*}
                  <Menu.Item
                    style={{ width: "auto" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      open(row.original.id);
                    }}
                  >
                    <Group>{t("actions:renewContract")}</Group>
                  </Menu.Item>*/}
                </Menu.Dropdown>
              </Menu>
            </Group>
          );
        },
      },
    ],
    [t, currentCompany]
  );

  const columns = useVisibleColumns<ContractLight, "price" | "actions">(baseColumns, visibleColumns);

  return (
    <Group dir={"col"} className={styles.tableContainer}>
      <DataTable
        onRowClick={undefined}
        data={localRows || contracts?.contracts || []}
        columns={columns}
        isLoading={false}
        mobileHeaderCell={2}
        showPagination={false}
        initialSort={initialSort ?? undefined}
        initialRowsPerPage={initialRowsPerPage}
      />
    </Group>
  );
};

export default ArchivedContractTable;
